import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getValueTier, getValueLabel, formatUSDValue } from '../utils/priceApi';
import { REWARD_SPLIT } from '../constants';
import { calculateBonusReward, getUserDistilBalance } from '../utils/holdingBonusManager';
import './PurifyConfirmationModal.css';

function PurifyConfirmationModal({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  objectCount,
  selectedObjects = [],
  isToiletMode = false,
  isDemoMode = false,
}) {
  const [totalValue, setTotalValue] = useState(0);
  const [highValueObjects, setHighValueObjects] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [highValueConfirmed, setHighValueConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // 💎 Holding bonus calculation — display hidden pre-TGE, re-enable at TGE
  // const userDistilBalance = getUserDistilBalance(null, true);
  const estimatedBaseReward = objectCount * 10;
  const userReward = estimatedBaseReward * REWARD_SPLIT.USER_PERCENTAGE;
  // const { finalReward: userReward, bonusAmount, tier } = calculateBonusReward(
  //   estimatedBaseReward * REWARD_SPLIT.USER_PERCENTAGE,
  //   userDistilBalance
  // );
  const protocolFee = estimatedBaseReward * REWARD_SPLIT.PROTOCOL_PERCENTAGE;
  const burnAmount = estimatedBaseReward * REWARD_SPLIT.BURN_PERCENTAGE;
  const hasHoldingBonus = false; // Re-enable at TGE: bonusAmount > 0
  
  // Calculate total USD value and identify high-value objects
  useEffect(() => {
    if (!isOpen || selectedObjects.length === 0) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    let total = 0;
    const highValue = [];
    
    selectedObjects.forEach(obj => {
      const value = obj?.mockUsdValue !== undefined ? obj.mockUsdValue : obj?.usdValue;
      if (value && value > 0) {
        total += value;
        if (value >= 100) {
          highValue.push({
            name: obj?.data?.display?.data?.name || 'Unknown Object',
            value: value
          });
        }
      }
    });
    
    setTotalValue(total);
    setHighValueObjects(highValue);
    setLoading(false);
  }, [isOpen, selectedObjects]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setHighValueConfirmed(false);
    }
  }, [isOpen]);
  
  // Check if user can confirm
  const canConfirm = () => {
    if (loading) return false;
    if (totalValue >= 100 || highValueObjects.length > 0) {
      return highValueConfirmed && confirmText === 'CONFIRM';
    }
    if (totalValue >= 10) {
      return confirmText === 'CONFIRM';
    }
    return true;
  };
  
  if (!isOpen) return null;

  const emoji = isToiletMode ? '🚽' : '🚀';
  const title = isToiletMode ? 'READY TO FLUSH IT ALL? 🚽' : 'READY TO SEND IT? 🚀';
  const confirmButtonText = isToiletMode ? '🚽 FLUSH IT ALL!' : '💎 SEND IT!';
  
  const isHighValue = totalValue >= 100 || highValueObjects.length > 0;
  const isMediumValue = totalValue >= 10 && !isHighValue;
  const isDangerZone = isHighValue;

  return (
    <AnimatePresence>
      <motion.div
        className="purify-confirmation-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
      >
        <motion.div
          className={`purify-confirmation-modal compact-modal ${isToiletMode ? 'toilet-mode' : ''} ${isDangerZone ? 'danger-zone' : ''}`}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Icon + Title */}
          <div className="confirmation-header-compact">
            <span className="confirmation-emoji-compact">{emoji}</span>
            <h2 className="confirmation-title-compact">{title}</h2>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="value-calculating">
              <span className="calculating-spinner">⏳</span>
              <span>Calculating values...</span>
            </div>
          )}

          {/* 🔥 COMPACT: Info Grid - Value, Objects, Action in one row */}
          {!loading && (
            <div className="info-grid-compact">
              {totalValue > 0 && (
                <div className={`info-stat ${isHighValue ? 'stat-danger' : isMediumValue ? 'stat-warning' : 'stat-normal'}`}>
                  <div className="stat-label">Total Value</div>
                  <div className="stat-value">{formatUSDValue(totalValue)}</div>
                </div>
              )}
              <div className="info-stat">
                <div className="stat-label">Targets</div>
                <div className="stat-value">{objectCount} scam{objectCount > 1 ? 's' : ''}</div>
              </div>
              <div className="info-stat">
                <div className="stat-label">Action</div>
                <div className="stat-value">Total Obliteration</div>
              </div>
            </div>
          )}

          {/* HIGH VALUE WARNING - Compact */}
          {!loading && isHighValue && (
            <motion.div 
              className="high-value-warning-compact"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="warning-header-compact">
                <span className="warning-icon">🚨</span>
                <span className="warning-text">
                  Deleting {highValueObjects.length} object{highValueObjects.length > 1 ? 's' : ''} worth ${100}+ each!
                </span>
              </div>
              
              {highValueObjects.length > 0 && highValueObjects.length <= 3 && (
                <div className="high-value-chips">
                  {highValueObjects.map((obj, idx) => (
                    <div key={idx} className="value-chip">
                      {obj.name}: {formatUSDValue(obj.value)}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="safety-checkbox-compact">
                <input 
                  type="checkbox" 
                  id="confirm-high-value"
                  checked={highValueConfirmed}
                  onChange={(e) => setHighValueConfirmed(e.target.checked)}
                />
                <label htmlFor="confirm-high-value">
                  I understand I'm deleting high-value assets
                </label>
              </div>
            </motion.div>
          )}

          {/* TYPE TO CONFIRM */}
          {!loading && (isMediumValue || isHighValue) && (
            <div className="type-confirm-compact">
              <label>Type "CONFIRM":</label>
              <input 
                type="text"
                placeholder="CONFIRM"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                autoComplete="off"
                className={confirmText === 'CONFIRM' ? 'confirmed' : ''}
              />
            </div>
          )}

          {/* 🔥 COMPACT: Rewards in Visual Badge Style */}
          {!loading && (
            <div className="rewards-compact">
              <div className="reward-main">
                <span className="reward-icon">💎</span>
                <div className="reward-info">
                  <div className="reward-amount">{userReward.toFixed(2)} $DISTIL</div>
                  {/* Holding bonus badge — re-enable at TGE */}
                  {/* {hasHoldingBonus && (
                    <div className="reward-bonus">{tier.badge} +{((tier.bonus - 1) * 100).toFixed(0)}% Bonus!</div>
                  )} */}
                </div>
              </div>
              
              <div className="reward-fees-compact">
                <span>Protocol: {protocolFee.toFixed(1)}</span>
                <span className="burn-text">🔥 Burn: {burnAmount.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Message - DEGEN ENERGY */}
          <div className="confirmation-message-compact">
            <p className="nuke-text">
              About to NUKE <strong>{objectCount}</strong> piece{objectCount > 1 ? 's' : ''} of garbage
            </p>
            <p className="no-mercy">NO MERCY. NO REMORSE.</p>
          </div>

          {/* Demo Mode Banner */}
          {isDemoMode && (
            <div className="demo-mode-banner">
              🎮 Demo mode — <strong>connect wallet</strong> to earn real $DISTIL
            </div>
          )}

          {/* Warning */}
          <div className="warning-footer">
            ⚠️ This action <strong>cannot be undone</strong>
          </div>

          {/* Buttons */}
          <div className="confirmation-buttons-compact">
            <button 
              className="confirmation-btn cancel-btn-compact" 
              onClick={onCancel}
            >
              {isToiletMode ? 'NAH, KEEP IT' : 'NAH, KEEP MY TRASH'}
            </button>
            <button 
              className={`confirmation-btn confirm-btn-compact ${isToiletMode ? 'toilet' : ''} ${isDangerZone ? 'danger' : ''}`}
              onClick={onConfirm}
              disabled={!canConfirm()}
            >
              {isDangerZone ? '🚨 DELETE ANYWAY' : confirmButtonText}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PurifyConfirmationModal;
